import { WidgetsInterface } from '~constants/WidgetsNames';
import { getWidgetsConfig } from '~utils';

import { initWidgetShadowDom } from './createWidget';

export interface CartWidgetController {
  setOpen: (isOpen: boolean) => void;
  setItemCount: (count: number) => void;
}

export interface CartWidgetHTMLElement extends HTMLElement {
  _controller: CartWidgetController;
  _itemCount: number;
  _pendingOpen: boolean | null;
  open: boolean;
  itemCount: number;
  show(): void;
  close(): void;
  toggle(): void;
}

export const createCartWidget = (widget: WidgetsInterface) => {
  if (getWidgetsConfig('data-enable-cart') !== 'true') return;

  class CartWidgetElement extends HTMLElement {
    // -1 is a sentinel meaning "not initialised yet". The first setItemCount call
    // therefore always dispatches a cartchange event, even when the count happens
    // to be 0, so integrators get an explicit ready signal.
    _itemCount = -1;

    _pendingOpen: boolean | null = null;

    _controller: CartWidgetController = {
      setItemCount: (count) => this.syncItemCount(count),
      setOpen: (isOpen) => {
        this._pendingOpen = isOpen;
      },
    };

    static get observedAttributes() {
      return ['open'];
    }

    connectedCallback() {
      initWidgetShadowDom(this, widget);
    }

    get open(): boolean {
      return this.hasAttribute('open');
    }

    set open(value: boolean) {
      this.toggleAttribute('open', value);
    }

    get itemCount(): number {
      return Math.max(0, this._itemCount);
    }

    attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null) {
      if (name !== 'open' || oldValue === newValue) return;

      const isOpen = newValue !== null;
      this._controller.setOpen(isOpen);

      this.dispatchEvent(
        new CustomEvent('toggle', {
          bubbles: true,
          detail: {
            newState: isOpen ? 'open' : 'closed',
            oldState: isOpen ? 'closed' : 'open',
          },
        }),
      );
    }

    show() {
      this.open = true;
    }

    close() {
      this.open = false;
    }

    toggle() {
      this.open = !this.open;
    }

    private syncItemCount(count: number) {
      if (this._itemCount === count) return;
      this._itemCount = count;
      this.dataset.itemCount = String(count);
      this.dispatchEvent(
        new CustomEvent('cartchange', {
          bubbles: true,
          detail: { itemCount: count },
        }),
      );
    }
  }

  const tagName = `${widget.id}-widget`;
  if (!customElements.get(tagName)) {
    customElements.define(tagName, CartWidgetElement);
  }
};
